import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Product } from '../types/product';
import type { LangStrings } from '../i18n/en';
import { generateDocumentPdfBlob } from '../utils/generatePdf';
import { api, API_BASE, ApiError, getCsrfToken, getCurrentLang } from '../lib/api';

interface CartEmailParams {
  cartId: number;
  products: Product[];
  recipients: string[];
  subject: string;
  body: string;
  includedTypes: string[];
  documentTitle?: string;
  strings?: LangStrings;
  contactPerson?: string;
  companyName?: string;
  companyWebsite?: string;
}

interface CartEmailResponse {
  message: string;
}

async function sendCartEmail(params: CartEmailParams): Promise<CartEmailResponse> {
  const { blob, fileName } = await generateDocumentPdfBlob(
    params.products,
    params.documentTitle,
    params.strings,
    params.contactPerson,
    params.companyName,
    params.companyWebsite,
  );

  const formData = new FormData();
  params.recipients.forEach((r) => formData.append('recipients[]', r));
  formData.append('subject', params.subject);
  formData.append('body', params.body);
  formData.append('pdf', blob, fileName);
  params.includedTypes.forEach((type) => formData.append('included_types[]', type));

  const res = await fetch(`${API_BASE}/carts/${params.cartId}/email`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Accept-Language': getCurrentLang(),
      'X-XSRF-TOKEN': getCsrfToken(),
    },
    body: formData,
  });

  if (res.status === 401) {
    localStorage.removeItem('vivatis-logged-in');
    window.location.href = '/login';
    throw new ApiError(401, 'Unauthenticated');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.message ?? 'Request failed', body);
  }

  return res.json();
}

export function useCartEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendCartEmail,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['carts'] }),
  });
}

/**
 * File types the cart's products actually carry. The send dialog uses it so it
 * never offers a checkbox that would do nothing (VIVATISPDB-445).
 *
 * The product ids are part of the key on purpose: queries never go stale in
 * this app, so adding a product has to produce a new key to be picked up.
 */
export function useCartFileTypes(cartId: number | undefined, productIds: number[]) {
  return useQuery({
    queryKey: ['cart-file-types', cartId, [...productIds].sort((a, b) => a - b).join(',')],
    queryFn: () => api<{ types: string[] }>(`/carts/${cartId}/file-types`),
    enabled: cartId !== undefined,
  });
}
